import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {pluralize} from 'BenchlingUtil';
import {IDENTITY_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import notifications from 'notifications';
import EmptySidebarIcon from 'sidebar/EmptySidebarIcon';
import SidebarItem from 'sidebar/SidebarItem';
import useOnMount from 'ui-library/hooks/useOnMount';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import * as nyc from 'ui-library/NYC';
import {assertNever} from 'util/Types';

import {CurrentTenantQuery} from './__generated/CurrentTenantQuery';
import ProjectPolicyTable from './ProjectPolicyTable';
import RegistryPolicyTable from './RegistryPolicyTable';

export const CURRENT_TENANT_QUERY = gql`
  query CurrentTenantQuery @skipAudit @engComponent(name: IDENTITY) {
    currentTenant {
      id
      displayName
    }
  }
`;

enum ResourceType {
  PROJECT = 'PROJECT',
  REGISTRY = 'REGISTRY',
}

const RESOURCE_TYPE_TO_DISPLAY: {[key in ResourceType]: string} = {
  [ResourceType.PROJECT]: 'Projects',
  [ResourceType.REGISTRY]: 'Registries',
};

interface SidepanelProps {
  activeResourceType: ResourceType;
  onChangeActiveResourceType: (resourceType: ResourceType) => void;
}

function Sidepanel(props: SidepanelProps): JSX.Element {
  return (
    <>
      <nyc.Row className={css(styles.sidebarTitle)}>
        <EmptySidebarIcon />
        <strong>Resource Types</strong>
      </nyc.Row>
      {Object.keys(ResourceType).map((resourceType: ResourceType) => (
        <SidebarItem
          key={resourceType}
          active={props.activeResourceType === resourceType}
          name={RESOURCE_TYPE_TO_DISPLAY[resourceType]}
          icon={<EmptySidebarIcon />}
          clickHandler={() => {
            props.onChangeActiveResourceType(resourceType);
          }}
        />
      ))}
    </>
  );
}

export interface UserDataAccessTabProps {
  userId: string;
}

function UserDataAccessTabSection(props: {resourceType: string; children: JSX.Element}): JSX.Element | null {
  const result = useBenchlingQuery<CurrentTenantQuery>(CURRENT_TENANT_QUERY);
  if (result.type === QueryResultType.PENDING) {
    return (
      <div className={css(styles.userDataAccessTabSection)}>
        <CenteredHorizontalThrobber />
      </div>
    );
  }
  if (result.type === QueryResultType.ERROR) {
    notifications.error('Could not load tenant details; try reloading the page.');
    return null;
  }
  return (
    <div className={css(styles.userDataAccessTabSection)}>
      <p className={css(styles.description)}>
        The user has access to the following {pluralize(2, props.resourceType.toLowerCase())} in{' '}
        {result.data.currentTenant.displayName} that you have access to. You can see which access policies the
        user has been granted per {props.resourceType.toLowerCase()} and view more details on their effective
        permissions.
      </p>
      <p className={css(styles.description)}>
        This view is limited to {pluralize(2, props.resourceType.toLowerCase())} you can also access. As a
        result you may not see a complete list of all the {pluralize(2, props.resourceType.toLowerCase())} for
        which this user is a collaborator.
      </p>
      {props.children}
    </div>
  );
}

function PolicyTable(props: {resourceType: ResourceType; userId: string}): JSX.Element {
  switch (props.resourceType) {
    case ResourceType.PROJECT:
      return <ProjectPolicyTable userId={props.userId} />;
    case ResourceType.REGISTRY:
      return <RegistryPolicyTable userId={props.userId} />;
    default:
      assert(false, `Unexpected resource type ${assertNever(props.resourceType)}`);
      return <></>;
  }
}

export default function UserDataAccessTab(props: UserDataAccessTabProps): JSX.Element {
  useOnMount(() => {
    analytics.trackEvent(IDENTITY_ANALYTICS_ACTIONS.OPENED_PARTY_DATA_ACCESS_TAB, {
      objectType: 'user',
      objectId: props.userId,
    });
  });
  const [activeResourceType, setActiveResourceType] = useState(ResourceType.PROJECT);
  return (
    <nyc.Container size="lg" className={css(styles.container)}>
      <nyc.Row className={css(styles.containerRow)}>
        <nyc.Col lg={4} className={classNames('sidepanel', css(styles.sidepanelColumn))}>
          <Sidepanel
            activeResourceType={activeResourceType}
            onChangeActiveResourceType={setActiveResourceType}
          />
        </nyc.Col>
        <nyc.Col lg={18}>
          <UserDataAccessTabSection resourceType={activeResourceType}>
            <PolicyTable resourceType={activeResourceType} userId={props.userId} />
          </UserDataAccessTabSection>
        </nyc.Col>
      </nyc.Row>
    </nyc.Container>
  );
}

const styles = StyleSheet.create({
  description: {fontSize: '13px', maxWidth: '1000px'},
  sidepanelColumn: {
    padding: '0 15px',
    position: 'static',
  },
  container: {
    height: '100%',
    paddingLeft: 0,
  },
  containerRow: {
    overflow: 'hidden',
    height: '100%',
  },
  sidebarTitle: {
    padding: '20px 15px 5px 15px',
  },
  userDataAccessTabSection: {
    padding: '35px 29px',
  },
});
